import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';

// ---------------------------------------------------------------------------
// TipTapResumeEditor
//
// WYSIWYG editor styled to look like an 8.5×11 Word letter page. Drop-in
// replacement for the plain contentEditable div the editor was using before.
//
// • Keeps the existing toolbar API: parent can still call formatText('bold')
//   etc. via the imperative handle (see TipTapResumeEditorHandle).
// • Reports content out as plain text — so the existing AI / JSON / Docmosis
//   pipeline downstream doesn't need to change at all.
// • Reasonable defaults that mirror the Docmosis output families: Calibri-
//   like fallback stack, 11pt body, comfortable letter-paper margins, real
//   bullets, headings, alignment.
// ---------------------------------------------------------------------------

export interface TipTapResumeEditorHandle {
  /** Get the current document as plain text (newline-separated). */
  getText: () => string;
  /** Replace the editor contents with the given plain text. */
  setText: (text: string) => void;
  /** Run a TipTap chain command by name. Used by the existing toolbar. */
  command: (name:
    | 'bold' | 'italic' | 'underline'
    | 'bulletList' | 'orderedList'
    | 'alignLeft' | 'alignCenter' | 'alignRight'
    | 'undo' | 'redo',
  ) => void;
  /** Returns the live TipTap editor instance (or null if not mounted). */
  getEditor: () => Editor | null;
}

interface Props {
  /** Initial plain-text content. Each line becomes a paragraph. */
  initialText: string;
  /**
   * Preferred initial content as styled HTML — used when the parent has
   * converted the rendered .docx via Mammoth so the editor inherits the
   * actual Word document's bold, headings, lists, tables, etc. When this
   * is provided, `initialText` is ignored on first mount.
   */
  initialHtml?: string;
  /** Fires whenever the editor content changes. Receives plain text. */
  onChange: (text: string) => void;
  /** Optional className for the outer wrapper. */
  className?: string;
  /**
   * Optional template family hint that nudges the editor styles (font /
   * accent color) toward the chosen Docmosis design. Not pixel-perfect —
   * just a visual cue so the user feels they're editing the right look.
   */
  templateFamily?:
    | 'classic' | 'navy-serif' | 'professional'
    | 'burgundy' | 'charcoal-gold' | 'minimal' | 'slate-sans';
  /**
   * Fires whenever TipTap's internal history changes — i.e. after every
   * transaction. The parent can use these flags to drive the disabled
   * state of its Undo / Redo buttons. Without this, the parent would have
   * to read `editor.can().undo()` off a ref, which React never re-renders
   * for, so the buttons end up perpetually disabled.
   */
  onHistoryChange?: (canUndo: boolean, canRedo: boolean) => void;
}

const FONT_BY_FAMILY: Record<string, string> = {
  classic:        'Calibri, "Segoe UI", Arial, sans-serif',
  navy:           '"Cambria", "Georgia", "Times New Roman", serif',
  'navy-serif':   '"Cambria", "Georgia", "Times New Roman", serif',
  professional:  'Calibri, "Segoe UI", Arial, sans-serif',
  burgundy:       '"Cambria", "Georgia", "Times New Roman", serif',
  'charcoal-gold':'Calibri, "Segoe UI", Arial, sans-serif',
  minimal:        '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  'slate-sans':   '"Inter", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
};

const ACCENT_BY_FAMILY: Record<string, string> = {
  classic:        '#1f3a68',
  'navy-serif':   '#0B1C3F',
  professional:   '#1f3a68',
  burgundy:       '#7a1f2b',
  'charcoal-gold':'#C9A84C',
  minimal:        '#111827',
  'slate-sans':   '#334155',
};

/** Convert plain text → minimal HTML so paragraphs render as blocks. */
const textToHtml = (text: string): string => {
  if (!text) return '<p></p>';
  return text
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join('');
};

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const TipTapResumeEditor = forwardRef<TipTapResumeEditorHandle, Props>(
  ({ initialText, initialHtml, onChange, className, templateFamily = 'professional', onHistoryChange }, ref) => {
    const fontStack = FONT_BY_FAMILY[templateFamily] ?? FONT_BY_FAMILY.professional;
    const accent = ACCENT_BY_FAMILY[templateFamily] ?? ACCENT_BY_FAMILY.professional;

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          // StarterKit includes history; keep its 50-step buffer.
        }),
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        // Tables: Mammoth converts the Docmosis Skills + Career-Highlights
        // tables into <table> markup, and these extensions let TipTap render
        // them as real editable tables instead of dropping the content.
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
      ],
      content: initialHtml && initialHtml.trim() ? initialHtml : textToHtml(initialText),
      editorProps: {
        attributes: {
          // The editor itself is the page. Margins simulate Word's 1" margins.
          class: 'tiptap-resume-page focus:outline-none',
          spellcheck: 'true',
        },
      },
      onUpdate: ({ editor }) => {
        // Report plain text upstream so the JSON / AI pipeline is unchanged.
        onChange(editor.getText());
      },
      onTransaction: ({ editor }) => {
        // Every transaction (typing, command, setContent) refreshes the
        // history flags so the parent's Undo / Redo buttons reflect what's
        // actually available. Without this, the parent reads off a ref and
        // never re-renders, so the buttons sit perpetually disabled.
        if (onHistoryChange) {
          onHistoryChange(editor.can().undo(), editor.can().redo());
        }
      },
    });

    // If the parent swaps the underlying document (e.g. AI auto-apply or
    // template-switch refresh), force-reset the editor's content. When a
    // styled HTML version arrives (Mammoth-converted .docx), prefer it
    // over the plain-text version so the editor inherits the real Word
    // formatting (bold, headings, lists, tables).
    useEffect(() => {
      if (!editor) return;
      if (initialHtml && initialHtml.trim()) {
        const currentHtml = editor.getHTML();
        if (currentHtml === initialHtml) return;
        editor.commands.setContent(initialHtml, false);
        return;
      }
      const current = editor.getText();
      if (current === initialText) return;
      editor.commands.setContent(textToHtml(initialText), false);
    }, [editor, initialText, initialHtml]);

    useImperativeHandle(ref, () => ({
      getText: () => editor?.getText() ?? '',
      setText: (text: string) => {
        if (!editor) return;
        editor.commands.setContent(textToHtml(text), false);
      },
      command: (name) => {
        if (!editor) return;
        switch (name) {
          case 'bold':         editor.chain().focus().toggleBold().run(); break;
          case 'italic':       editor.chain().focus().toggleItalic().run(); break;
          case 'underline':    editor.chain().focus().toggleUnderline().run(); break;
          case 'bulletList':   editor.chain().focus().toggleBulletList().run(); break;
          case 'orderedList':  editor.chain().focus().toggleOrderedList().run(); break;
          case 'alignLeft':    editor.chain().focus().setTextAlign('left').run(); break;
          case 'alignCenter':  editor.chain().focus().setTextAlign('center').run(); break;
          case 'alignRight':   editor.chain().focus().setTextAlign('right').run(); break;
          case 'undo':         editor.chain().focus().undo().run(); break;
          case 'redo':         editor.chain().focus().redo().run(); break;
        }
      },
      getEditor: () => editor,
    }), [editor]);

    return (
      <div className={`tiptap-resume-wrapper ${className || ''}`}>
        {/* Scoped styles. Kept inline so each instance can re-style by template
            family without leaking globally. */}
        <style>{`
          .tiptap-resume-wrapper {
            /* Letter-paper "stage" around the page. */
            background: #f3f4f6;
            min-height: 100%;
            padding: 1.25rem;
            overflow: auto;
          }
          .tiptap-resume-wrapper .tiptap-resume-page {
            /* Real 8.5x11 letter page with Word-style 1" margins. */
            background: #ffffff;
            color: #1a1a2e;
            font-family: ${fontStack};
            font-size: 11pt;
            line-height: 1.45;
            box-shadow: 0 4px 24px rgba(15, 23, 42, 0.12);
            border: 1px solid #e5e7eb;
            margin: 0 auto;
            width: 8.5in;
            min-height: 11in;
            padding: 1in;
            box-sizing: border-box;
            border-radius: 2px;
            outline: none;
          }
          .tiptap-resume-wrapper .tiptap-resume-page p {
            margin: 0 0 6pt 0;
          }
          .tiptap-resume-wrapper .tiptap-resume-page h1,
          .tiptap-resume-wrapper .tiptap-resume-page h2,
          .tiptap-resume-wrapper .tiptap-resume-page h3 {
            color: ${accent};
            font-weight: 700;
            margin: 14pt 0 4pt 0;
          }
          .tiptap-resume-wrapper .tiptap-resume-page h1 { font-size: 18pt; }
          .tiptap-resume-wrapper .tiptap-resume-page h2 {
            font-size: 12pt;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            border-bottom: 1px solid ${accent}40;
            padding-bottom: 3pt;
          }
          .tiptap-resume-wrapper .tiptap-resume-page h3 { font-size: 11pt; }
          .tiptap-resume-wrapper .tiptap-resume-page ul,
          .tiptap-resume-wrapper .tiptap-resume-page ol {
            margin: 0 0 8pt 0;
            padding-left: 22pt;
          }
          .tiptap-resume-wrapper .tiptap-resume-page li {
            margin: 0 0 3pt 0;
          }
          .tiptap-resume-wrapper .tiptap-resume-page strong { font-weight: 700; }
          .tiptap-resume-wrapper .tiptap-resume-page em { font-style: italic; }
          .tiptap-resume-wrapper .tiptap-resume-page u { text-decoration: underline; }
          /* Tables — Mammoth converts Docmosis Skills + Highlights tables
             into <table>. Style them so they look like Word table cells. */
          .tiptap-resume-wrapper .tiptap-resume-page table {
            border-collapse: collapse;
            margin: 6pt 0;
            width: 100%;
            table-layout: fixed;
          }
          .tiptap-resume-wrapper .tiptap-resume-page td,
          .tiptap-resume-wrapper .tiptap-resume-page th {
            border: 1px solid #e5e7eb;
            padding: 4pt 6pt;
            vertical-align: top;
            font-size: 10.5pt;
          }
          .tiptap-resume-wrapper .tiptap-resume-page th {
            font-weight: 700;
            background: #f9fafb;
          }
          /* Image support for the few templates that embed accent shapes. */
          .tiptap-resume-wrapper .tiptap-resume-page img {
            max-width: 100%;
            height: auto;
          }
          /* Selection looks like Word's accent. */
          .tiptap-resume-wrapper .tiptap-resume-page ::selection {
            background: ${accent}33;
          }
          /* Print-style page edge feedback */
          .tiptap-resume-wrapper .tiptap-resume-page:focus-within {
            box-shadow: 0 4px 24px rgba(15, 23, 42, 0.18);
          }
        `}</style>
        <EditorContent editor={editor} />
      </div>
    );
  },
);

TipTapResumeEditor.displayName = 'TipTapResumeEditor';
